import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import { AppLanguageSwitcher } from '@/components/ui/AppLanguageSwitcher'
import { useTranslation } from '@/i18n/useTranslation'
import { ShopQuayWordmark } from '@/components/brand/ShopQuayWordmark'
import { DEMO_EXPLORE_LINK, SHOWCASE_SHOPS, type ShowcaseShop } from '@/lib/showcaseShops'

function ShowcaseShopLink({
  shop,
  className,
  role,
  onClick,
  children,
}: {
  shop: ShowcaseShop
  className: string
  role?: string
  onClick: () => void
  children: ReactNode
}) {
  if (shop.href) {
    return (
      <a href={shop.href} className={className} role={role} onClick={onClick}>
        {children}
      </a>
    )
  }
  return (
    <Link to={shop.to} className={className} role={role} onClick={onClick}>
      {children}
    </Link>
  )
}

const PublicHeader = () => {
  const { t } = useTranslation('common')
  const location = useLocation()
  const [demoOpen, setDemoOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuId = useId()
  const mobilePanelId = useId()

  useEffect(() => {
    setMobileOpen(false)
    setDemoOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!demoOpen) return
    const onPointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setDemoOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDemoOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [demoOpen])

  useEffect(() => {
    if (!mobileOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [mobileOpen])

  const linkClass =
    'text-[0.95rem] font-medium text-[var(--mq-ink)]/65 transition hover:text-[var(--mq-ink)]'
  const mobileLinkClass =
    'block rounded-lg px-3 py-2.5 text-base font-medium text-[var(--mq-ink)]/80 transition hover:bg-[var(--mq-ink)]/[0.04] hover:text-[var(--mq-ink)]'

  return (
    <header className="marketing-surface sticky top-0 z-40 border-b border-[var(--mq-ink)]/8 bg-[var(--mq-sky)]/85 backdrop-blur-md">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4 py-3.5 md:py-4">
          <div className="min-w-0 shrink-0">
            <ShopQuayWordmark to="/" variant="light" />
          </div>

          <nav className="hidden items-center gap-4 lg:gap-5 xl:gap-7 md:flex" aria-label={t('auth.publicHeader.menu')}>
            <Link to="/pricing" className={linkClass}>
              {t('auth.publicHeader.pricing')}
            </Link>
            <Link to="/help" className={linkClass}>
              {t('auth.publicHeader.help')}
            </Link>
            <Link to="/onboarding" className={linkClass}>
              {t('auth.publicHeader.onboarding')}
            </Link>

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className={`inline-flex items-center gap-1 ${linkClass}`}
                aria-expanded={demoOpen}
                aria-haspopup="menu"
                aria-controls={menuId}
                onClick={() => setDemoOpen((open) => !open)}
              >
                {t('auth.publicHeader.demoMenu')}
                <ChevronDownIcon
                  className={`h-4 w-4 transition ${demoOpen ? 'rotate-180' : ''}`}
                  aria-hidden
                />
              </button>

              {demoOpen ? (
                <div
                  id={menuId}
                  role="menu"
                  aria-label={t('auth.publicHeader.demoMenu')}
                  className="absolute left-0 top-full z-50 mt-2 w-72 rounded-xl border border-[var(--mq-ink)]/10 bg-[var(--mq-sky)] p-1.5 shadow-lg shadow-[var(--mq-ink)]/10"
                >
                  {SHOWCASE_SHOPS.filter((shop) => shop.kind === 'demo').map((shop) => (
                    <ShowcaseShopLink
                      key={shop.to}
                      shop={shop}
                      role="menuitem"
                      className="flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 transition hover:bg-[var(--mq-ink)]/[0.04]"
                      onClick={() => setDemoOpen(false)}
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-[var(--mq-ink)]">
                          {t(`auth.publicHeader.${shop.nameKey}`)}
                        </span>
                        <span className="mt-0.5 block text-xs text-[var(--mq-ink)]/55">
                          {t('auth.publicHeader.demoShopHint')}
                        </span>
                      </span>
                      <span className="mt-0.5 shrink-0 rounded-md bg-[var(--mq-ink)]/8 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--mq-ink)]/70">
                        {t('auth.publicHeader.badgeDemo')}
                      </span>
                    </ShowcaseShopLink>
                  ))}
                  <Link
                    to={DEMO_EXPLORE_LINK.to}
                    role="menuitem"
                    className="flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 transition hover:bg-[var(--mq-ink)]/[0.04]"
                    onClick={() => setDemoOpen(false)}
                  >
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-[var(--mq-ink)]">
                        {t(`auth.publicHeader.${DEMO_EXPLORE_LINK.nameKey}`)}
                      </span>
                      <span className="mt-0.5 block text-xs text-[var(--mq-ink)]/55">
                        {t(`auth.publicHeader.${DEMO_EXPLORE_LINK.hintKey}`)}
                      </span>
                    </span>
                    <span className="mt-0.5 shrink-0 rounded-md bg-[var(--mq-water)]/15 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--mq-water)]">
                      {t('auth.publicHeader.badgeErp')}
                    </span>
                  </Link>
                  <div className="my-1.5 border-t border-[var(--mq-ink)]/8" role="separator" />
                  {SHOWCASE_SHOPS.filter((shop) => shop.kind === 'live').map((shop) => (
                    <ShowcaseShopLink
                      key={shop.to}
                      shop={shop}
                      role="menuitem"
                      className="flex items-start justify-between gap-3 rounded-lg px-3 py-2.5 transition hover:bg-[var(--mq-ink)]/[0.04]"
                      onClick={() => setDemoOpen(false)}
                    >
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-[var(--mq-ink)]">
                          {t(`auth.publicHeader.${shop.nameKey}`)}
                        </span>
                        <span className="mt-0.5 block text-xs text-[var(--mq-ink)]/55">
                          {t('auth.publicHeader.liveShopHint')}
                        </span>
                      </span>
                      <span className="mt-0.5 shrink-0 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-emerald-800">
                        {t('auth.publicHeader.badgeLive')}
                      </span>
                    </ShowcaseShopLink>
                  ))}
                </div>
              ) : null}
            </div>

            <Link to="/partners" className={`hidden xl:inline ${linkClass}`}>
              {t('auth.publicHeader.partners')}
            </Link>
          </nav>

          <div className="flex items-center justify-end gap-2 sm:gap-3">
            <AppLanguageSwitcher variant="compact" className="hidden sm:block" />
            <Link
              to="/auth/staff/login"
              className="hidden whitespace-nowrap rounded-lg border border-[var(--mq-ink)]/12 bg-white/50 px-3.5 py-2 text-sm font-semibold text-[var(--mq-ink)]/85 backdrop-blur-sm transition hover:border-[var(--mq-water)]/40 hover:bg-white hover:text-[var(--mq-water)] lg:inline-flex lg:items-center"
            >
              {t('auth.publicHeader.staffLogin')}
            </Link>
            <Link
              to="/signup"
              className="inline-flex shrink-0 items-center justify-center rounded-lg bg-[var(--mq-accent)] px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-[var(--mq-accent-deep)] sm:px-4"
            >
              <span className="xl:hidden">{t('auth.publicHeader.startFreeTrialShort')}</span>
              <span className="hidden xl:inline">{t('auth.publicHeader.startFreeTrial')}</span>
            </Link>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg p-2 text-[var(--mq-ink)]/70 transition hover:bg-[var(--mq-ink)]/[0.05] hover:text-[var(--mq-ink)] md:hidden"
              aria-expanded={mobileOpen}
              aria-controls={mobilePanelId}
              aria-label={
                mobileOpen ? t('auth.publicHeader.closeMenu') : t('auth.publicHeader.openMenu')
              }
              onClick={() => setMobileOpen((open) => !open)}
            >
              {mobileOpen ? (
                <XMarkIcon className="h-6 w-6" aria-hidden />
              ) : (
                <Bars3Icon className="h-6 w-6" aria-hidden />
              )}
            </button>
          </div>
        </div>

        {mobileOpen ? (
          <nav
            id={mobilePanelId}
            className="border-t border-[var(--mq-ink)]/8 pb-4 pt-2 md:hidden"
            aria-label={t('auth.publicHeader.menu')}
          >
            <div className="flex flex-col gap-0.5">
              <Link to="/pricing" className={mobileLinkClass} onClick={() => setMobileOpen(false)}>
                {t('auth.publicHeader.pricing')}
              </Link>
              <Link to="/help" className={mobileLinkClass} onClick={() => setMobileOpen(false)}>
                {t('auth.publicHeader.help')}
              </Link>
              <Link to="/onboarding" className={mobileLinkClass} onClick={() => setMobileOpen(false)}>
                {t('auth.publicHeader.onboarding')}
              </Link>

              <p className="px-3 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-[var(--mq-ink)]/45">
                {t('auth.publicHeader.demoMenu')}
              </p>
              {SHOWCASE_SHOPS.filter((shop) => shop.kind === 'demo').map((shop) => (
                <ShowcaseShopLink
                  key={shop.to}
                  shop={shop}
                  className={`${mobileLinkClass} flex items-center justify-between gap-3`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="min-w-0">
                    <span className="block">{t(`auth.publicHeader.${shop.nameKey}`)}</span>
                    <span className="mt-0.5 block text-xs font-normal text-[var(--mq-ink)]/50">
                      {t('auth.publicHeader.demoShopHint')}
                    </span>
                  </span>
                  <span className="shrink-0 rounded-md bg-[var(--mq-ink)]/8 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--mq-ink)]/70">
                    {t('auth.publicHeader.badgeDemo')}
                  </span>
                </ShowcaseShopLink>
              ))}
              <Link
                to={DEMO_EXPLORE_LINK.to}
                className={`${mobileLinkClass} flex items-center justify-between gap-3`}
                onClick={() => setMobileOpen(false)}
              >
                <span className="min-w-0">
                  <span className="block">{t(`auth.publicHeader.${DEMO_EXPLORE_LINK.nameKey}`)}</span>
                  <span className="mt-0.5 block text-xs font-normal text-[var(--mq-ink)]/50">
                    {t(`auth.publicHeader.${DEMO_EXPLORE_LINK.hintKey}`)}
                  </span>
                </span>
                <span className="shrink-0 rounded-md bg-[var(--mq-water)]/15 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-[var(--mq-water)]">
                  {t('auth.publicHeader.badgeErp')}
                </span>
              </Link>
              {SHOWCASE_SHOPS.filter((shop) => shop.kind === 'live').map((shop) => (
                <ShowcaseShopLink
                  key={shop.to}
                  shop={shop}
                  className={`${mobileLinkClass} flex items-center justify-between gap-3`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="min-w-0">
                    <span className="block">{t(`auth.publicHeader.${shop.nameKey}`)}</span>
                    <span className="mt-0.5 block text-xs font-normal text-[var(--mq-ink)]/50">
                      {t('auth.publicHeader.liveShopHint')}
                    </span>
                  </span>
                  <span className="shrink-0 rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-emerald-800">
                    {t('auth.publicHeader.badgeLive')}
                  </span>
                </ShowcaseShopLink>
              ))}

              <Link to="/partners" className={mobileLinkClass} onClick={() => setMobileOpen(false)}>
                {t('auth.publicHeader.partners')}
              </Link>

              <div className="mt-3 flex items-center justify-between gap-3 border-t border-[var(--mq-ink)]/8 px-3 pt-3">
                <AppLanguageSwitcher variant="compact" />
                <Link
                  to="/auth/staff/login"
                  className="inline-flex items-center justify-center rounded-lg border border-[var(--mq-ink)]/12 bg-white/70 px-3.5 py-2 text-sm font-semibold text-[var(--mq-ink)]/85 transition hover:border-[var(--mq-water)]/40 hover:text-[var(--mq-water)]"
                  onClick={() => setMobileOpen(false)}
                >
                  {t('auth.publicHeader.staffLogin')}
                </Link>
              </div>
            </div>
          </nav>
        ) : null}
      </div>
    </header>
  )
}

export default PublicHeader
